import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Calendar, TrendingUp, TrendingDown, DollarSign, PiggyBank, Percent,
  ArrowUpRight, ArrowDownRight, RefreshCw, FileSpreadsheet, Upload, 
  FileText, AlertTriangle, ShieldAlert, Trash2, UploadCloud, Plus, X, Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Month names for financial year (May=1 to April=12)
const MONTH_NAMES = ["May", "June", "July", "August", "September", "October", 
                     "November", "December", "January", "February", "March", "April"];

export default function FinancialYear() {
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearData, setYearData] = useState(null);
  const [cashflowData, setCashflowData] = useState(null);
  const [enhancedCashflow, setEnhancedCashflow] = useState(null);
  const [vatSummary, setVatSummary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [debtOwed, setDebtOwed] = useState(0);
  const [manualCashOnHand, setManualCashOnHand] = useState(null); // Manual override for cash on hand
  const [manualExpense, setManualExpense] = useState(null); // Manual override for expense
  const [uploadingMonth, setUploadingMonth] = useState(null);
  const [deletingMonth, setDeletingMonth] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [monthToDelete, setMonthToDelete] = useState(null);
  const [showAddTransactionDialog, setShowAddTransactionDialog] = useState(false);
  const [addTransactionMonth, setAddTransactionMonth] = useState(null);
  const [newTransaction, setNewTransaction] = useState({
    description: '',
    type: 'expense',
    amount_full: '',
    is_vat_claimable: false,
    manual_vat_amount: '',
    date: ''
  });
  const monthFileInputRefs = useRef({});
  const [editingVat, setEditingVat] = useState({}); // Track which cells are being edited
  const [vatValues, setVatValues] = useState({}); // Store temp VAT values during edit

  // Save manual VAT for a month
  const saveManualVat = async (monthName, vatIn, vatOut) => {
    try {
      const formData = new FormData();
      formData.append('fy_year', selectedYear);
      formData.append('month', monthName);
      if (vatIn !== null && vatIn !== undefined && vatIn !== '') {
        formData.append('vat_in', vatIn);
      }
      if (vatOut !== null && vatOut !== undefined && vatOut !== '') {
        formData.append('vat_out', vatOut);
      }
      
      await axios.post(`${API_URL}/cashflow/manual-vat`, formData);
      toast.success(`VAT saved for ${monthName}`);
      fetchYearData(selectedYear);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save VAT'));
    }
  };

  // Toggle VAT paid status for a month
  const toggleVatPaid = async (monthName, vatPaid) => {
    try {
      const formData = new FormData();
      formData.append('fy_year', selectedYear);
      formData.append('month', monthName);
      formData.append('vat_paid', vatPaid);
      
      await axios.post(`${API_URL}/cashflow/vat-paid`, formData);
      toast.success(`VAT paid ${vatPaid ? 'confirmed' : 'unmarked'} for ${monthName}`);
      fetchYearData(selectedYear);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update VAT status'));
    }
  };

  // Handle VAT input change
  const handleVatChange = (monthName, type, value) => {
    setVatValues(prev => ({
      ...prev,
      [`${monthName}_${type}`]: value
    }));
  };

  // Handle VAT input blur (save)
  const handleVatBlur = (monthName, type) => {
    const key = `${monthName}_${type}`;
    const value = vatValues[key];
    
    if (value !== undefined && value !== '') {
      const numValue = parseFloat(value) || 0;
      if (type === 'in') {
        saveManualVat(monthName, numValue, null);
      } else {
        saveManualVat(monthName, null, numValue);
      }
    }
    
    setEditingVat(prev => ({ ...prev, [key]: false }));
  };

  // Calculate outstanding VAT (total outstanding after pool and arrangements)
  const outstandingVat = vatSummary?.total_outstanding_after_pool || 0;
  
  // Get current period VAT (pending - not yet due)
  const currentPeriodVat = vatSummary?.periods?.find(p => p.status === 'pending')?.balance || 0;
  
  // SARS Arrangements made
  const sarsArrangements = vatSummary?.total_arrangements || 0;
  
  // Calculate VAT actually due NOW (excluding current pending period)
  const vatActuallyDueNow = outstandingVat - currentPeriodVat;
  
  // Calculate CORRECTED totals from enhanced cashflow (using bank statement expenses)
  const correctedTotals = React.useMemo(() => {
    if (!enhancedCashflow?.months) return null;
    
    let totalIncFull = 0, totalVatOut = 0, totalIncExcl = 0;
    let totalExpFull = 0, totalVatIn = 0, totalExpExcl = 0;
    
    enhancedCashflow.months.forEach(em => {
      const stmtOpening = em.statement_opening;
      const stmtClosing = em.statement_closing;
      const incFull = em.income_full || 0;
      const opening = stmtOpening != null ? stmtOpening : (em.opening || 0);
      const closing = stmtClosing != null ? stmtClosing : (em.closing || 0);
      
      // Income calculations
      totalIncFull += incFull;
      const manualVatOut = em.manual_vat_out;
      const rawIncVat = em.income_vat || 0;
      const vatOut = manualVatOut != null ? manualVatOut : (rawIncVat > 0 ? rawIncVat : (incFull * 0.15 / 1.15));
      totalVatOut += vatOut;
      totalIncExcl += (incFull - vatOut);
      
      // Expense calculations - from bank statement
      let expFull;
      if (stmtOpening != null && stmtClosing != null && incFull > 0) {
        expFull = opening + incFull - closing;
        if (expFull < 0) expFull = 0;
      } else {
        expFull = em.expense_full || 0;
      }
      totalExpFull += expFull;
      
      // VAT In: MANUAL ONLY - default to 0
      const manualVatIn = em.manual_vat_in;
      const vatIn = manualVatIn != null ? manualVatIn : 0;
      totalVatIn += vatIn;
      totalExpExcl += (expFull - vatIn);
    });
    
    return {
      income_full: totalIncFull,
      vat_out: totalVatOut,
      income_excl: totalIncExcl,
      expense_full: totalExpFull,
      vat_in: totalVatIn,
      expense_excl: totalExpExcl,
      net_profit: totalIncExcl - totalExpExcl,
      vat_due: totalVatOut - totalVatIn  // VAT payable to SARS
    };
  }, [enhancedCashflow]);
  
  // Calculate UNPAID VAT liability (months where VAT Paid checkbox is unchecked)
  const unpaidVatLiability = React.useMemo(() => {
    if (!enhancedCashflow?.months) return { vatOutUnpaid: 0, vatInUnpaid: 0, netUnpaid: 0, unpaidMonths: [] };
    
    let vatOutUnpaid = 0;
    let vatInUnpaid = 0;
    const unpaidMonths = [];
    
    enhancedCashflow.months.forEach(em => {
      // Only count months that have data and VAT Paid is false/unchecked
      const hasData = (em.income_full || 0) > 0 || (em.expense_full || 0) > 0;
      const vatPaid = em.vat_paid || false;
      
      if (hasData && !vatPaid) {
        const stmtOpening = em.statement_opening;
        const stmtClosing = em.statement_closing;
        const incFull = em.income_full || 0;
        const opening = stmtOpening != null ? stmtOpening : (em.opening || 0);
        const closing = stmtClosing != null ? stmtClosing : (em.closing || 0);
        
        // VAT Out calculation
        const manualVatOut = em.manual_vat_out;
        const rawIncVat = em.income_vat || 0;
        const vatOut = manualVatOut != null ? manualVatOut : (rawIncVat > 0 ? rawIncVat : (incFull * 0.15 / 1.15));
        vatOutUnpaid += vatOut;
        
        // VAT In: MANUAL ONLY - default to 0
        const manualVatIn = em.manual_vat_in;
        const vatIn = manualVatIn != null ? manualVatIn : 0;
        vatInUnpaid += vatIn;
        
        unpaidMonths.push({
          month: em.month,
          vatOut: vatOut,
          vatIn: vatIn,
          netVat: vatOut - vatIn
        });
      }
    });
    
    return {
      vatOutUnpaid,
      vatInUnpaid,
      netUnpaid: vatOutUnpaid - vatInUnpaid,
      unpaidMonths
    };
  }, [enhancedCashflow]);
  
  // Calculate PAID VAT (months where VAT Paid checkbox IS ticked - this is a cash outflow/expense)
  const paidVatAmount = React.useMemo(() => {
    if (!enhancedCashflow?.months) return { vatPaidTotal: 0, paidMonths: [] };
    
    let vatPaidTotal = 0;
    const paidMonths = [];
    
    enhancedCashflow.months.forEach(em => {
      // Only count months that have data AND VAT Paid is true/checked
      const hasData = (em.income_full || 0) > 0 || (em.expense_full || 0) > 0;
      const vatPaid = em.vat_paid || false;
      
      if (hasData && vatPaid) {
        const incFull = em.income_full || 0;
        
        // VAT Out calculation (this is the VAT that was paid to SARS)
        const manualVatOut = em.manual_vat_out;
        const rawIncVat = em.income_vat || 0;
        const vatOut = manualVatOut != null ? manualVatOut : (rawIncVat > 0 ? rawIncVat : (incFull * 0.15 / 1.15));
        
        // VAT In: MANUAL ONLY - default to 0
        const manualVatIn = em.manual_vat_in;
        const vatIn = manualVatIn != null ? manualVatIn : 0;
        
        // Net VAT paid to SARS = VAT Out - VAT In
        const netVatPaid = vatOut - vatIn;
        vatPaidTotal += netVatPaid;
        
        paidMonths.push({
          month: em.month,
          vatOut: vatOut,
          vatIn: vatIn,
          netVatPaid: netVatPaid
        });
      }
    });
    
    return {
      vatPaidTotal,
      paidMonths
    };
  }, [enhancedCashflow]);
  
  // Use corrected values or fallback to old values
  const incomeExclVat = correctedTotals?.income_excl || yearData?.totals?.income_net || 0;
  const expenseExclVat = correctedTotals?.expense_excl || yearData?.totals?.expenses_net || 0;
  const netProfitBeforeVAT = correctedTotals?.net_profit || yearData?.totals?.net_profit || 0;
  const totalVatOut = correctedTotals?.vat_out || 0;
  const totalVatIn = correctedTotals?.vat_in || 0;
  const vatDue = correctedTotals?.vat_due || 0;
  
  // Total Income (Full) and Expense (Full) for simple TRUE PROFIT calculation
  const totalIncomeFull = correctedTotals?.income_full || 0;
  const calculatedExpenseFull = correctedTotals?.expense_full || 0;
  
  // Use manual expense if set, otherwise use calculated
  const totalExpenseFull = manualExpense !== null ? manualExpense : calculatedExpenseFull;
  
  // Cash on Hand = manual override OR last month's closing balance
  const calculatedCashOnHand = React.useMemo(() => {
    if (!enhancedCashflow?.months) return 0;
    const lastMonthWithData = enhancedCashflow.months.slice().reverse().find(m => 
      m.statement_closing != null || m.closing > 0
    );
    return lastMonthWithData?.statement_closing ?? lastMonthWithData?.closing ?? 0;
  }, [enhancedCashflow]);
  
  // Use manual cash on hand if set, otherwise use calculated
  const cashOnHand = manualCashOnHand !== null ? manualCashOnHand : calculatedCashOnHand;
  
  // VAT Due (Unpaid) - only VAT from months where checkbox is NOT ticked
  // This reduces when VAT Paid checkbox is clicked
  const vatDueUnpaid = unpaidVatLiability.netUnpaid;
  
  // TRUE PROFIT = (Income + Cash on Hand) - (Expense + VAT Due + Debt)
  const trueProfitFinal = (totalIncomeFull + cashOnHand) - (totalExpenseFull + vatDueUnpaid + debtOwed);
  
  // Danger Zone - check if losing money
  const isDangerZone = trueProfitFinal < 0;

  useEffect(() => {
    fetchFinancialYears();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchYearData(selectedYear);
    }
  }, [selectedYear]);

  const fetchFinancialYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/years`);
      setFinancialYears(response.data.financial_years || []);
      if (response.data.financial_years?.length > 0) {
        setSelectedYear(response.data.financial_years[0]);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load financial years'));
    }
  };

  const fetchYearData = async (fy) => {
    setLoading(true);
    try {
      const [yearRes, cashflowRes, vatRes, debtRes, enhancedRes] = await Promise.all([
        axios.get(`${API_URL}/finance/by-year?financial_year=${encodeURIComponent(fy)}`),
        axios.get(`${API_URL}/finance/cashflow-by-year?financial_year=${encodeURIComponent(fy)}`),
        axios.get(`${API_URL}/finance/vat-summary?financial_year=${encodeURIComponent(fy)}`),
        axios.get(`${API_URL}/finance/debt?financial_year=${encodeURIComponent(fy)}`),
        axios.get(`${API_URL}/cashflow/year/summary?fy_year=${encodeURIComponent(fy)}&opening_balance=0`).catch(() => null)
      ]);
      setYearData(yearRes.data);
      setCashflowData(cashflowRes.data);
      setVatSummary(vatRes.data);
      setDebtOwed(debtRes.data?.amount || 0);
      if (enhancedRes?.data) {
        setEnhancedCashflow(enhancedRes.data);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load year data'));
    } finally {
      setLoading(false);
    }
  };

  const saveDebt = async (amount) => {
    try {
      await axios.post(`${API_URL}/finance/debt`, {
        financial_year: selectedYear,
        amount: amount,
        description: 'Business debt'
      });
      toast.success('Debt saved');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save debt'));
    }
  };

  const handleDebtChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setDebtOwed(value);
  };

  const handleDebtBlur = () => {
    if (selectedYear) {
      saveDebt(debtOwed);
    }
  };

  // Handle manual Cash on Hand
  const handleCashOnHandChange = (e) => {
    const value = e.target.value === '' ? null : parseFloat(e.target.value) || 0;
    setManualCashOnHand(value);
  };

  // Handle manual Expense
  const handleExpenseChange = (e) => {
    const value = e.target.value === '' ? null : parseFloat(e.target.value) || 0;
    setManualExpense(value);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/finance/import-bank-recon`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { imported_transactions, skipped_duplicates } = response.data;
      
      if (skipped_duplicates > 0) {
        toast.warning(`Imported ${imported_transactions} transactions. ${skipped_duplicates} duplicates skipped.`);
      } else {
        toast.success(`Imported ${imported_transactions} transactions`);
      }
      
      fetchFinancialYears();
      if (selectedYear) fetchYearData(selectedYear);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Import failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }

    setUploadingPdf(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/finance/import-bank-statement-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.imported_transactions > 0) {
        toast.success(`Imported ${response.data.imported_transactions} transactions from PDF`);
        if (response.data.gl_codes_learned?.length > 0) {
          toast.info(`Learned ${response.data.gl_codes_learned.length} GL codes`);
        }
        fetchFinancialYears();
        if (selectedYear) fetchYearData(selectedYear);
      } else {
        toast.warning(response.data.message || 'No transactions found in PDF');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'PDF import failed'));
    } finally {
      setUploadingPdf(false);
      e.target.value = '';
    }
  };

  // Month-specific upload handler - auto-splits by transaction date
  const handleMonthUpload = async (e, monthName) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingMonth(monthName);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fy_year', selectedYear);
    // Note: month is NOT sent - system auto-detects from transaction dates

    try {
      const response = await axios.post(`${API_URL}/cashflow/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Show detailed result
      const monthsAffected = response.data.months_affected?.join(', ') || monthName;
      if (response.data.duplicates_skipped > 0) {
        toast.success(`Imported ${response.data.transactions_imported} transactions to: ${monthsAffected}`);
        toast.info(`${response.data.duplicates_skipped} duplicates skipped`);
      } else {
        toast.success(`Imported ${response.data.transactions_imported} transactions to: ${monthsAffected}`);
      }
      
      fetchYearData(selectedYear);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Upload failed'));
    } finally {
      setUploadingMonth(null);
      e.target.value = '';
    }
  };

  // Month delete confirmation
  const confirmDeleteMonth = (monthName) => {
    setMonthToDelete({ name: monthName });
    setShowDeleteDialog(true);
  };

  // Month-specific delete handler - PROPERLY CLEARS ALL DATA
  const handleMonthDelete = async () => {
    if (!monthToDelete) return;
    
    setDeletingMonth(monthToDelete.name);
    setShowDeleteDialog(false);

    try {
      // Use the new cashflow clear endpoint that properly deletes ALL data
      const response = await axios.delete(`${API_URL}/cashflow/month/clear`, {
        params: {
          fy_year: selectedYear,
          month: monthToDelete.name,
          clear_type: 'all'
        }
      });
      
      toast.success(`Cleared ${monthToDelete.name}: ${response.data.total_deleted} records deleted`);
      
      // Refresh data to show updated values
      fetchYearData(selectedYear);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Delete failed'));
    } finally {
      setDeletingMonth(null);
      setMonthToDelete(null);
    }
  };

  // Add transaction dialog
  const openAddTransaction = (monthName) => {
    setAddTransactionMonth(monthName);
    setNewTransaction({
      description: '',
      type: 'expense',
      amount_full: '',
      is_vat_claimable: false,
      manual_vat_amount: '',
      date: ''
    });
    setShowAddTransactionDialog(true);
  };

  // Handle add transaction
  const handleAddTransaction = async () => {
    if (!newTransaction.description || !newTransaction.amount_full) {
      toast.error('Please fill in description and amount');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('fy_year', selectedYear);
      formData.append('month', addTransactionMonth);
      formData.append('description', newTransaction.description);
      formData.append('transaction_type', newTransaction.type);
      formData.append('amount_full', newTransaction.amount_full);
      formData.append('is_vat_claimable', newTransaction.is_vat_claimable);
      if (newTransaction.manual_vat_amount) {
        formData.append('manual_vat_amount', newTransaction.manual_vat_amount);
      }
      if (newTransaction.date) {
        formData.append('date', newTransaction.date);
      }

      const response = await axios.post(`${API_URL}/cashflow/transaction`, formData);
      
      const vatInfo = response.data.vat_amount > 0 
        ? ` (VAT: R ${response.data.vat_amount?.toLocaleString()})` 
        : ' (No VAT)';
      toast.success(`Added ${newTransaction.type}: R ${parseFloat(newTransaction.amount_full).toLocaleString()}${vatInfo}`);
      
      setShowAddTransactionDialog(false);
      fetchYearData(selectedYear);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add transaction'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 max-w-[95%] mx-auto" data-testid="financial-year-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Financial Year</h1>
            <p className="text-muted-foreground mt-1">May to April financial period analysis</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-44">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {financialYears.map(fy => (
                  <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="cursor-pointer">
              <Button variant="outline" disabled={uploading} asChild>
                <span>
                  <Upload className={`h-4 w-4 mr-2 ${uploading ? 'animate-spin' : ''}`} />
                  {uploading ? 'Importing...' : 'Excel'}
                </span>
              </Button>
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImport} data-testid="excel-import-input" />
            </label>
            <label className="cursor-pointer">
              <Button variant="outline" disabled={uploadingPdf} asChild>
                <span>
                  <FileText className={`h-4 w-4 mr-2 ${uploadingPdf ? 'animate-spin' : ''}`} />
                  {uploadingPdf ? 'Importing...' : 'PDF'}
                </span>
              </Button>
              <input type="file" accept=".pdf" className="hidden" onChange={handlePdfImport} data-testid="pdf-import-input" />
            </label>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading financial data...</p>
          </div>
        ) : yearData ? (
          <>
            {/* Summary Cards - Simplified Layout */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              {/* Income (Full) */}
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <ArrowUpRight className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-green-700 truncate">Income (Full)</p>
                      <p className="text-lg font-bold text-green-700">{formatCurrency(totalIncomeFull)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Expense (Full) - Editable */}
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <ArrowDownRight className="h-6 w-6 text-red-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-red-700 truncate">Expense (Full)</p>
                      <input
                        type="number"
                        value={manualExpense !== null ? manualExpense : calculatedExpenseFull}
                        onChange={handleExpenseChange}
                        className="text-lg font-bold text-red-700 bg-transparent border-b border-red-300 w-full focus:outline-none focus:border-red-500"
                        placeholder="0"
                      />
                      {manualExpense !== null && (
                        <p className="text-[10px] text-red-500">manual</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* VAT Due (Unpaid) - reduces when VAT Paid checkbox is ticked */}
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Percent className="h-6 w-6 text-purple-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-purple-700 truncate">VAT Due (Unpaid)</p>
                      <p className="text-lg font-bold text-purple-700">{formatCurrency(vatDueUnpaid)}</p>
                      <p className="text-[10px] text-purple-600">{unpaidVatLiability.unpaidMonths.length} months</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cash on Hand - Editable */}
              <Card className={cashOnHand >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className={`h-6 w-6 flex-shrink-0 ${cashOnHand >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
                    <div className="min-w-0">
                      <p className={`text-xs truncate ${cashOnHand >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>Cash on Hand</p>
                      <input
                        type="number"
                        value={manualCashOnHand !== null ? manualCashOnHand : calculatedCashOnHand}
                        onChange={handleCashOnHandChange}
                        className={`text-lg font-bold bg-transparent border-b w-full focus:outline-none ${cashOnHand >= 0 ? 'text-blue-700 border-blue-300 focus:border-blue-500' : 'text-orange-700 border-orange-300 focus:border-orange-500'}`}
                        placeholder="0"
                      />
                      {manualCashOnHand !== null && (
                        <p className="text-[10px] text-blue-500">manual</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Debt Owed */}
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-amber-700 truncate">Debt Owed</p>
                      <input
                        type="number"
                        value={debtOwed}
                        onChange={handleDebtChange}
                        onBlur={handleDebtBlur}
                        className="text-lg font-bold text-amber-700 bg-transparent border-b border-amber-300 w-full focus:outline-none focus:border-amber-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* TRUE PROFIT = (Income + Cash) - (Expense + VAT + Debt) */}
              <Card className={trueProfitFinal >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-100 border-red-400'}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    {trueProfitFinal >= 0 ? 
                      <PiggyBank className="h-6 w-6 text-emerald-600 flex-shrink-0" /> : 
                      <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0" />
                    }
                    <div className="min-w-0">
                      <p className={`text-xs truncate font-bold ${trueProfitFinal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        TRUE PROFIT
                      </p>
                      <p className={`text-lg font-bold ${trueProfitFinal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {formatCurrency(trueProfitFinal)}
                      </p>
                      <p className="text-[10px] text-gray-500">(Inc+Cash)-(Exp+VAT+Debt)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* DANGER ZONE INDICATOR */}
            {isDangerZone && (
              <Alert variant="destructive" className="bg-red-50 border-red-500 border-2" data-testid="danger-zone-alert">
                <ShieldAlert className="h-5 w-5" />
                <AlertTitle className="text-red-800 font-bold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  DANGER ZONE - Business Operating at a Loss
                </AlertTitle>
                <AlertDescription className="text-red-700">
                  <p className="mb-2">
                    Your business shows a <strong>loss of {formatCurrency(Math.abs(trueProfitFinal))}</strong> for FY {selectedYear}.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-3 text-sm">
                    <div className="bg-green-100/60 rounded px-2 py-1">
                      <p className="text-xs text-green-600">Income (Full)</p>
                      <p className="font-bold text-green-700">+{formatCurrency(totalIncomeFull)}</p>
                    </div>
                    <div className="bg-blue-100/60 rounded px-2 py-1">
                      <p className="text-xs text-blue-600">+ Cash on Hand</p>
                      <p className="font-bold text-blue-700">+{formatCurrency(cashOnHand)}</p>
                    </div>
                    <div className="bg-red-100/60 rounded px-2 py-1">
                      <p className="text-xs text-red-600">- Expense (Full)</p>
                      <p className="font-bold text-red-700">-{formatCurrency(totalExpenseFull)}</p>
                    </div>
                    <div className="bg-purple-100/60 rounded px-2 py-1">
                      <p className="text-xs text-purple-600">- VAT Due (Unpaid)</p>
                      <p className="font-bold text-purple-700">-{formatCurrency(vatDueUnpaid)}</p>
                    </div>
                    <div className="bg-amber-100/60 rounded px-2 py-1">
                      <p className="text-xs text-amber-600">- Debt Owed</p>
                      <p className="font-bold text-amber-700">-{formatCurrency(debtOwed)}</p>
                    </div>
                    <div className="bg-red-200/60 rounded px-2 py-1">
                      <p className="text-xs text-red-600 font-semibold">= TRUE PROFIT</p>
                      <p className="font-bold text-red-800">{formatCurrency(trueProfitFinal)}</p>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Tabs for different views */}
            <Tabs defaultValue="cashflow" className="space-y-4">
              <TabsList>
                <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
                <TabsTrigger value="income">Income by GL</TabsTrigger>
                <TabsTrigger value="expenses">Expenses by GL</TabsTrigger>
                <TabsTrigger value="monthly">Monthly Summary</TabsTrigger>
              </TabsList>

              <TabsContent value="cashflow">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <DollarSign className="h-5 w-5" />
                      Monthly Cash Flow - FY {selectedYear}
                    </CardTitle>
                    <CardDescription>
                      Upload (CSV/XLSX/PDF), delete, or add transactions per month. VAT calculated at 15%.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    {cashflowData?.cashflow?.length > 0 ? (
                      <Table className="text-xs">
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="py-2 px-2 w-20">Month</TableHead>
                            <TableHead className="text-right py-2 px-1 text-gray-600">Opening</TableHead>
                            <TableHead className="text-right py-2 px-1 text-green-700 bg-green-50">Income (Full)</TableHead>
                            <TableHead className="text-right py-2 px-1 text-green-600 bg-green-50">VAT (Out)</TableHead>
                            <TableHead className="text-right py-2 px-1 text-green-800 bg-green-50">Income (Excl)</TableHead>
                            <TableHead className="text-right py-2 px-1 text-red-700 bg-red-50">Expense (Full)</TableHead>
                            <TableHead className="text-right py-2 px-1 text-red-600 bg-red-50">VAT (In)</TableHead>
                            <TableHead className="text-right py-2 px-1 text-red-800 bg-red-50">Expense (Excl)</TableHead>
                            <TableHead className="text-right py-2 px-1">Net Change</TableHead>
                            <TableHead className="text-right py-2 px-1 font-bold">Closing</TableHead>
                            <TableHead className="text-center py-2 px-1 w-16">VAT Paid</TableHead>
                            <TableHead className="text-center py-2 px-1 w-28">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {cashflowData.cashflow.map((m) => {
                            // Use enhanced data if available, otherwise fallback to legacy
                            const enhanced = enhancedCashflow?.months?.find(em => em.month === m.month_name);
                            
                            // Opening: prioritize statement_opening from bank statement
                            const stmtOpening = enhanced?.statement_opening;
                            const opening = stmtOpening != null 
                              ? stmtOpening 
                              : (enhanced?.opening ?? m.opening_cash);
                            
                            // Closing: prioritize statement_closing from bank statement
                            const stmtClosing = enhanced?.statement_closing;
                            const closing = stmtClosing != null 
                              ? stmtClosing 
                              : (enhanced?.closing ?? m.closing_cash);
                            
                            // Income totals (money IN)
                            const incFull = enhanced?.income_full || (m.income_net + (m.income_vat || 0));
                            
                            // EXPENSE: If we have statement balances, calculate from bank statement
                            // Expense = Opening + Income - Closing (all money that went OUT)
                            const hasStatementBalance = stmtOpening != null && stmtClosing != null;
                            let expFull;
                            if (hasStatementBalance && incFull > 0) {
                              // Calculate actual expense from bank statement: Opening + Income - Closing = Expense
                              expFull = opening + incFull - closing;
                              if (expFull < 0) expFull = 0; // Can't have negative expenses
                            } else {
                              expFull = enhanced?.expense_full || m.expenses_net;
                            }
                            
                            // VAT Out: use manual if set, then auto-calc 15%
                            const manualVatOut = enhanced?.manual_vat_out;
                            const rawIncVat = enhanced?.income_vat || m.income_vat || 0;
                            const incVat = manualVatOut != null ? manualVatOut : (rawIncVat > 0 ? rawIncVat : (incFull * 0.15 / 1.15));
                            const incExcl = incFull - incVat;
                            
                            // VAT In: MANUAL ONLY - default to 0, only use manual value if set
                            const manualVatIn = enhanced?.manual_vat_in;
                            const expVat = manualVatIn != null ? manualVatIn : 0;  // Default to 0 - user must enter manually
                            const expExcl = expFull - expVat;
                            
                            // Net Change: calculated from statement balances
                            const netChange = hasStatementBalance
                              ? (stmtClosing - stmtOpening)
                              : (enhanced?.net_change || m.net_change);
                            
                            const hasManualVatIn = manualVatIn != null;
                            const hasManualVatOut = manualVatOut != null;
                            const vatPaid = enhanced?.vat_paid || false;
                            
                            // Keys for editing state
                            const vatInKey = `${m.month_name}_in`;
                            const vatOutKey = `${m.month_name}_out`;
                            
                            return (
                              <TableRow key={m.financial_month} className="hover:bg-muted/30">
                                <TableCell className="font-medium py-2 px-2">{m.month_name}</TableCell>
                                <TableCell className={`text-right py-1 px-1 ${hasStatementBalance ? 'text-blue-600 font-medium' : 'text-muted-foreground'}`}>
                                  {formatCurrency(opening)}
                                  {enhanced?.statement_opening !== null && enhanced?.statement_opening !== undefined && (
                                    <span className="text-[10px] block text-blue-500">from stmt</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right text-green-700 py-1 px-1 bg-green-50/30 font-medium">{formatCurrency(incFull)}</TableCell>
                                {/* Editable VAT Out */}
                                <TableCell className="text-right text-green-600 py-1 px-1 bg-green-50/30">
                                  {editingVat[vatOutKey] ? (
                                    <input
                                      type="number"
                                      step="0.01"
                                      className="w-20 text-right text-xs p-0.5 border rounded bg-white"
                                      defaultValue={manualVatOut ?? incVat.toFixed(2)}
                                      onChange={(e) => handleVatChange(m.month_name, 'out', e.target.value)}
                                      onBlur={() => handleVatBlur(m.month_name, 'out')}
                                      onKeyDown={(e) => e.key === 'Enter' && handleVatBlur(m.month_name, 'out')}
                                      autoFocus
                                    />
                                  ) : (
                                    <span 
                                      className={`cursor-pointer hover:bg-green-100 px-1 rounded ${hasManualVatOut ? 'font-medium text-green-700' : ''}`}
                                      onClick={() => setEditingVat(prev => ({ ...prev, [vatOutKey]: true }))}
                                      title="Click to edit VAT Out"
                                    >
                                      {formatCurrency(incVat)}
                                      {hasManualVatOut && <span className="text-[9px] block text-green-600">manual</span>}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right text-green-800 py-1 px-1 bg-green-50/30">{formatCurrency(incExcl)}</TableCell>
                                <TableCell className="text-right text-red-700 py-1 px-1 bg-red-50/30 font-medium">{formatCurrency(expFull)}</TableCell>
                                {/* Editable VAT In */}
                                <TableCell className="text-right text-red-600 py-1 px-1 bg-red-50/30">
                                  {editingVat[vatInKey] ? (
                                    <input
                                      type="number"
                                      step="0.01"
                                      className="w-20 text-right text-xs p-0.5 border rounded bg-white"
                                      defaultValue={manualVatIn ?? expVat.toFixed(2)}
                                      onChange={(e) => handleVatChange(m.month_name, 'in', e.target.value)}
                                      onBlur={() => handleVatBlur(m.month_name, 'in')}
                                      onKeyDown={(e) => e.key === 'Enter' && handleVatBlur(m.month_name, 'in')}
                                      autoFocus
                                    />
                                  ) : (
                                    <span 
                                      className={`cursor-pointer hover:bg-red-100 px-1 rounded ${hasManualVatIn ? 'font-medium text-red-700' : ''}`}
                                      onClick={() => setEditingVat(prev => ({ ...prev, [vatInKey]: true }))}
                                      title="Click to edit VAT In"
                                    >
                                      {formatCurrency(expVat)}
                                      {hasManualVatIn && <span className="text-[9px] block text-red-600">manual</span>}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right text-red-800 py-1 px-1 bg-red-50/30">{formatCurrency(expExcl)}</TableCell>
                                <TableCell className={`text-right py-1 px-1 font-medium ${netChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(netChange)}
                                </TableCell>
                                <TableCell className={`text-right font-bold py-1 px-1 ${closing >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                                  {formatCurrency(closing)}
                                  {enhanced?.statement_closing !== null && enhanced?.statement_closing !== undefined && (
                                    <span className="text-[10px] block text-blue-500">from stmt</span>
                                  )}
                                </TableCell>
                                {/* VAT Paid Checkbox */}
                                <TableCell className="text-center py-1 px-1">
                                  <Checkbox
                                    checked={vatPaid}
                                    onCheckedChange={(checked) => toggleVatPaid(m.month_name, checked)}
                                    className={vatPaid ? 'border-green-500 bg-green-500 data-[state=checked]:bg-green-500' : ''}
                                  />
                                </TableCell>
                                <TableCell className="py-1 px-1">
                                  <div className="flex items-center justify-center gap-0.5">
                                    {/* Add Transaction */}
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-6 w-6 p-0 hover:bg-emerald-100"
                                      onClick={() => openAddTransaction(m.month_name)}
                                      title="Add Transaction"
                                    >
                                      <Plus className="h-3.5 w-3.5 text-emerald-600" />
                                    </Button>
                                    {/* Upload button */}
                                    <label className="cursor-pointer">
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-6 w-6 p-0 hover:bg-blue-100"
                                        disabled={uploadingMonth === m.month_name}
                                        asChild
                                        title="Upload CSV/XLSX/PDF"
                                      >
                                        <span>
                                          <UploadCloud className={`h-3.5 w-3.5 text-blue-600 ${uploadingMonth === m.month_name ? 'animate-spin' : ''}`} />
                                        </span>
                                      </Button>
                                      <input 
                                        type="file" 
                                        accept=".csv,.xlsx,.xls,.pdf" 
                                        className="hidden" 
                                        onChange={(e) => handleMonthUpload(e, m.month_name)}
                                      />
                                    </label>
                                    {/* Delete button */}
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="h-6 w-6 p-0 hover:bg-red-100"
                                      disabled={deletingMonth === m.month_name}
                                      onClick={() => confirmDeleteMonth(m.month_name)}
                                      title="Delete Month Data"
                                    >
                                      <Trash2 className={`h-3.5 w-3.5 text-red-600 ${deletingMonth === m.month_name ? 'animate-spin' : ''}`} />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow className="bg-muted font-bold border-t-2">
                            <TableCell className="py-2 px-2">TOTAL</TableCell>
                            <TableCell></TableCell>
                            <TableCell className="text-right text-green-700 py-2 px-1 bg-green-100">
                              {formatCurrency(enhancedCashflow?.totals?.income_full || (cashflowData.year_totals?.income_net + cashflowData.cashflow?.reduce((sum, m) => sum + (m.income_vat || 0), 0)))}
                            </TableCell>
                            <TableCell className="text-right text-green-600 py-2 px-1 bg-green-100">
                              {(() => {
                                // Sum up VAT Out: use manual if set, else auto-calc
                                let totalVatOut = 0;
                                enhancedCashflow?.months?.forEach(em => {
                                  const incFull = em.income_full || 0;
                                  const manualVatOut = em.manual_vat_out;
                                  const rawVat = em.income_vat || 0;
                                  const vatOut = manualVatOut != null ? manualVatOut : (rawVat > 0 ? rawVat : (incFull * 0.15 / 1.15));
                                  totalVatOut += vatOut;
                                });
                                return formatCurrency(totalVatOut || (enhancedCashflow?.totals?.income_full * 0.15 / 1.15) || 0);
                              })()}
                            </TableCell>
                            <TableCell className="text-right text-green-800 py-2 px-1 bg-green-100">
                              {(() => {
                                // Sum up Income Excl (Full - VAT Out)
                                let totalExcl = 0;
                                enhancedCashflow?.months?.forEach(em => {
                                  const incFull = em.income_full || 0;
                                  const manualVatOut = em.manual_vat_out;
                                  const rawVat = em.income_vat || 0;
                                  const vatOut = manualVatOut != null ? manualVatOut : (rawVat > 0 ? rawVat : (incFull * 0.15 / 1.15));
                                  totalExcl += (incFull - vatOut);
                                });
                                return formatCurrency(totalExcl || enhancedCashflow?.totals?.income_excl || 0);
                              })()}
                            </TableCell>
                            <TableCell className="text-right text-red-700 py-2 px-1 bg-red-100">
                              {(() => {
                                // Calculate total expense from bank statement: Opening + Income - Closing for each month
                                let totalExpense = 0;
                                enhancedCashflow?.months?.forEach(em => {
                                  const stmtOpening = em.statement_opening;
                                  const stmtClosing = em.statement_closing;
                                  const incFull = em.income_full || 0;
                                  const opening = stmtOpening != null ? stmtOpening : (em.opening || 0);
                                  const closing = stmtClosing != null ? stmtClosing : (em.closing || 0);
                                  
                                  // Calculate expense from bank statement if we have statement balances
                                  let expFull;
                                  if (stmtOpening != null && stmtClosing != null && incFull > 0) {
                                    expFull = opening + incFull - closing;
                                    if (expFull < 0) expFull = 0;
                                  } else {
                                    expFull = em.expense_full || 0;
                                  }
                                  totalExpense += expFull;
                                });
                                return formatCurrency(totalExpense);
                              })()}
                            </TableCell>
                            <TableCell className="text-right text-red-600 py-2 px-1 bg-red-100">
                              {(() => {
                                // Sum up VAT In: MANUAL ONLY - only count manually entered values
                                let totalVatIn = 0;
                                enhancedCashflow?.months?.forEach(em => {
                                  const manualVatIn = em.manual_vat_in;
                                  // Only add if manually set, otherwise 0
                                  totalVatIn += (manualVatIn != null ? manualVatIn : 0);
                                });
                                return formatCurrency(totalVatIn);
                              })()}
                            </TableCell>
                            <TableCell className="text-right text-red-800 py-2 px-1 bg-red-100">
                              {(() => {
                                // Sum up Expense Excl = Expense Full - VAT In (manual only)
                                let totalExcl = 0;
                                enhancedCashflow?.months?.forEach(em => {
                                  const stmtOpening = em.statement_opening;
                                  const stmtClosing = em.statement_closing;
                                  const incFull = em.income_full || 0;
                                  const opening = stmtOpening != null ? stmtOpening : (em.opening || 0);
                                  const closing = stmtClosing != null ? stmtClosing : (em.closing || 0);
                                  
                                  // Calculate expense from bank statement
                                  let expFull;
                                  if (stmtOpening != null && stmtClosing != null && incFull > 0) {
                                    expFull = opening + incFull - closing;
                                    if (expFull < 0) expFull = 0;
                                  } else {
                                    expFull = em.expense_full || 0;
                                  }
                                  
                                  // VAT In is manual only - default to 0
                                  const manualVatIn = em.manual_vat_in;
                                  const vatIn = manualVatIn != null ? manualVatIn : 0;
                                  totalExcl += (expFull - vatIn);
                                });
                                return formatCurrency(totalExcl);
                              })()}
                            </TableCell>
                            <TableCell className={`text-right py-2 px-1 font-bold`}>
                              {(() => {
                                // Calculate total Net Change: sum of (Closing - Opening) for each month
                                // OR Income Full - Expense Full = actual cash movement
                                let totalNetChange = 0;
                                enhancedCashflow?.months?.forEach(em => {
                                  const stmtOpening = em.statement_opening;
                                  const stmtClosing = em.statement_closing;
                                  const incFull = em.income_full || 0;
                                  const opening = stmtOpening != null ? stmtOpening : (em.opening || 0);
                                  const closing = stmtClosing != null ? stmtClosing : (em.closing || 0);
                                  
                                  // Calculate expense from bank statement
                                  let expFull;
                                  if (stmtOpening != null && stmtClosing != null && incFull > 0) {
                                    expFull = opening + incFull - closing;
                                    if (expFull < 0) expFull = 0;
                                  } else {
                                    expFull = em.expense_full || 0;
                                  }
                                  
                                  // Net change for this month = Income - Expense
                                  const monthNetChange = incFull - expFull;
                                  totalNetChange += monthNetChange;
                                });
                                
                                const isPositive = totalNetChange >= 0;
                                return (
                                  <span className={isPositive ? 'text-green-700' : 'text-red-700'}>
                                    {formatCurrency(totalNetChange)}
                                  </span>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-right py-2 px-1">
                              {(() => {
                                // Final closing balance - use last month with statement data
                                const lastMonth = enhancedCashflow?.months?.slice().reverse().find(m => 
                                  m.statement_closing != null || m.closing > 0
                                );
                                const finalClosing = lastMonth?.statement_closing ?? lastMonth?.closing ?? 0;
                                return (
                                  <span className={finalClosing >= 0 ? 'text-emerald-700' : 'text-orange-700'}>
                                    {formatCurrency(finalClosing)}
                                  </span>
                                );
                              })()}
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">No cash flow data for this year</div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="income">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <ArrowUpRight className="h-5 w-5 text-green-600" />
                      Income by GL Code - FY {selectedYear}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {yearData.income_by_gl && Object.keys(yearData.income_by_gl).length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {Object.entries(yearData.income_by_gl)
                          .sort((a, b) => b[1] - a[1])
                          .map(([gl, amount]) => (
                            <div key={gl} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                              <span className="font-medium truncate mr-2">{gl}</span>
                              <span className="text-green-700 font-bold whitespace-nowrap">{formatCurrency(amount)}</span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">No income data</div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="expenses">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <ArrowDownRight className="h-5 w-5 text-red-600" />
                      Expenses by GL Code - FY {selectedYear}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {yearData.expenses_by_gl && Object.keys(yearData.expenses_by_gl).length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {Object.entries(yearData.expenses_by_gl)
                          .sort((a, b) => b[1] - a[1])
                          .map(([gl, amount]) => (
                            <div key={gl} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                              <span className="font-medium truncate mr-2">{gl}</span>
                              <span className="text-red-700 font-bold whitespace-nowrap">{formatCurrency(amount)}</span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">No expense data</div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="monthly">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Calendar className="h-5 w-5" />
                      Monthly Summary - FY {selectedYear}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    {yearData.monthly_summary ? (
                      <Table className="text-sm">
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="py-2 px-3">Month</TableHead>
                            <TableHead className="text-right py-2 px-2 text-green-700">Income</TableHead>
                            <TableHead className="text-right py-2 px-2 text-red-700">Expenses</TableHead>
                            <TableHead className="text-right py-2 px-3">Net</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(yearData.monthly_summary).map(([month, data]) => (
                            <TableRow key={month}>
                              <TableCell className="font-medium">{MONTH_NAMES[parseInt(month) - 1]}</TableCell>
                              <TableCell className="text-right text-green-600">{formatCurrency(data.income)}</TableCell>
                              <TableCell className="text-right text-red-600">{formatCurrency(data.expenses)}</TableCell>
                              <TableCell className="text-right">
                                <span className={data.net >= 0 ? 'text-emerald-600' : 'text-orange-600'}>
                                  {formatCurrency(data.net)}
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">No monthly data</div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <p className="text-sm text-muted-foreground">Income Entries</p>
                  <p className="text-2xl font-bold">{yearData.income_count || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-red-600" />
                  <p className="text-sm text-muted-foreground">Expense Entries</p>
                  <p className="text-2xl font-bold">{yearData.expenses_count || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <PiggyBank className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                  <p className="text-sm text-muted-foreground">GL Codes (Income)</p>
                  <p className="text-2xl font-bold">{Object.keys(yearData.income_by_gl || {}).length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <PiggyBank className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                  <p className="text-sm text-muted-foreground">GL Codes (Expenses)</p>
                  <p className="text-2xl font-bold">{Object.keys(yearData.expenses_by_gl || {}).length}</p>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-medium mb-2">No Financial Year Data</h3>
            <p className="text-muted-foreground mb-4">Import bank reconciliation data to get started</p>
            <label className="cursor-pointer">
              <Button asChild>
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Bank Recon
                </span>
              </Button>
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImport} />
            </label>
          </div>
        )}

        {/* Delete Month Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Delete Month Data
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete all income and expense data for <strong>{monthToDelete?.name}</strong> in FY {selectedYear}?
                <br /><br />
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleMonthDelete}>
                Delete All Data
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Transaction Dialog */}
        <Dialog open={showAddTransactionDialog} onOpenChange={setShowAddTransactionDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-emerald-600" />
                Add Transaction - {addTransactionMonth}
              </DialogTitle>
              <DialogDescription>
                VAT is NOT included by default. Check "VAT Claimable" to calculate VAT, or enter manually.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="txn-description">Description</Label>
                <Input
                  id="txn-description"
                  placeholder="e.g., Office Supplies"
                  value={newTransaction.description}
                  onChange={(e) => setNewTransaction({...newTransaction, description: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="txn-type">Type</Label>
                  <Select 
                    value={newTransaction.type} 
                    onValueChange={(v) => setNewTransaction({...newTransaction, type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="txn-amount">Amount (Full)</Label>
                  <Input
                    id="txn-amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newTransaction.amount_full}
                    onChange={(e) => setNewTransaction({...newTransaction, amount_full: e.target.value})}
                  />
                </div>
              </div>
              
              {/* VAT Options */}
              <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
                <Label className="text-sm font-medium">VAT Options</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="vat-claimable"
                    checked={newTransaction.is_vat_claimable}
                    onChange={(e) => setNewTransaction({
                      ...newTransaction, 
                      is_vat_claimable: e.target.checked,
                      manual_vat_amount: e.target.checked ? '' : newTransaction.manual_vat_amount
                    })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="vat-claimable" className="text-sm cursor-pointer">
                    VAT Claimable (auto-calc 15%)
                  </Label>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="manual-vat" className="text-sm text-muted-foreground">
                    OR enter manual VAT amount:
                  </Label>
                  <Input
                    id="manual-vat"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newTransaction.manual_vat_amount}
                    onChange={(e) => setNewTransaction({
                      ...newTransaction, 
                      manual_vat_amount: e.target.value,
                      is_vat_claimable: false  // Manual VAT takes priority
                    })}
                    disabled={newTransaction.is_vat_claimable}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="txn-date">Date (Optional)</Label>
                <Input
                  id="txn-date"
                  type="date"
                  value={newTransaction.date}
                  onChange={(e) => setNewTransaction({...newTransaction, date: e.target.value})}
                />
              </div>
              {/* VAT Preview */}
              {newTransaction.amount_full && (
                <div className="bg-muted p-3 rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span>Full Amount:</span>
                    <span className="font-medium">R {parseFloat(newTransaction.amount_full || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-purple-600">
                    <span>VAT ({newTransaction.is_vat_claimable ? '15%' : newTransaction.manual_vat_amount ? 'Manual' : 'None'}):</span>
                    <span className="font-medium">
                      R {(() => {
                        const amount = parseFloat(newTransaction.amount_full || 0);
                        if (newTransaction.manual_vat_amount) {
                          return parseFloat(newTransaction.manual_vat_amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                        } else if (newTransaction.is_vat_claimable) {
                          return (amount * 0.15 / 1.15).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                        }
                        return '0.00';
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between font-bold border-t pt-1 mt-1">
                    <span>Excl VAT:</span>
                    <span>
                      R {(() => {
                        const amount = parseFloat(newTransaction.amount_full || 0);
                        if (newTransaction.manual_vat_amount) {
                          return (amount - parseFloat(newTransaction.manual_vat_amount)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                        } else if (newTransaction.is_vat_claimable) {
                          return (amount - (amount * 0.15 / 1.15)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                        }
                        return amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                      })()}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddTransactionDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddTransaction} className="bg-emerald-600 hover:bg-emerald-700">
                Add Transaction
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
